export class ApiError extends Error {
    status;
    code;
    detail;
    constructor(status, code, message, detail = {}) {
        super(message);
        this.status = status;
        this.code = code;
        this.detail = detail;
        this.name = "ApiError";
    }
}
/** Thin typed wrapper over the server's HTTP surface. Throws ApiError for non-2xx. */
export class HttpClient {
    base;
    fetchFn;
    headers;
    constructor(opts = {}) {
        this.base = opts.baseUrl ?? "";
        this.fetchFn = opts.fetch ?? ((input, init) => fetch(input, init));
        this.headers = opts.headers ?? (() => ({}));
    }
    async request(method, path, body) {
        const res = await this.fetchFn(this.base + path, {
            method,
            credentials: "same-origin",
            headers: { ...(body !== undefined ? { "content-type": "application/json" } : {}), ...this.headers() },
            ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
        });
        if (res.status === 204)
            return undefined;
        const text = await res.text();
        let json = null;
        try {
            json = text ? JSON.parse(text) : null;
        }
        catch {
            json = null;
        }
        if (!res.ok) {
            const e = (json ?? {});
            const { error, message, ...detail } = e;
            throw new ApiError(res.status, error ?? "http_error", message ?? `${res.status} ${res.statusText}`, detail);
        }
        return json;
    }
    login(email, password) {
        return this.request("POST", "/auth/login", { email, password });
    }
    logout() {
        return this.request("POST", "/auth/logout");
    }
    meta() {
        return this.request("GET", "/api/meta");
    }
    search(q, limit = 50, offset = 0) {
        return this.request("GET", `/api/issues?q=${encodeURIComponent(q)}&limit=${limit}&offset=${offset}`);
    }
    issue(id) {
        return this.request("GET", `/api/issues/${id}`);
    }
    async comments(issueId) {
        return (await this.request("GET", `/api/issues/${issueId}/comments`)).comments;
    }
    async activity(issueId) {
        return (await this.request("GET", `/api/issues/${issueId}/activity`)).activity;
    }
    async links(issueId) {
        return (await this.request("GET", `/api/issues/${issueId}/links`)).links;
    }
    /** Issues whose text names this one (YouTrack's "Inward mentions"). */
    async mentions(issueId) {
        return (await this.request("GET", `/api/issues/${issueId}/mentions`)).issues;
    }
    watchers(issueId) {
        return this.request("GET", `/api/issues/${issueId}/watchers`);
    }
    async byKeys(keys) {
        if (keys.length === 0)
            return [];
        return (await this.request("GET", `/api/issues/by-key?keys=${encodeURIComponent(keys.join(","))}`)).issues;
    }
    async boards() {
        return (await this.request("GET", "/api/boards")).boards;
    }
    board(id) {
        return this.request("GET", `/api/boards/${id}`);
    }
    async positions(boardId) {
        return (await this.request("GET", `/api/boards/${boardId}/positions`)).positions;
    }
    async issueBoards(issueId) {
        return (await this.request("GET", `/api/issues/${issueId}/boards`)).boards;
    }
    /** Owner path for an attachment target: an issue, an article (M11) or either kind of unsaved draft. */
    ownerPath(t) {
        if ("issueId" in t)
            return `/api/issues/${t.issueId}`;
        if ("articleId" in t)
            return `/api/articles/${t.articleId}`;
        return "draftId" in t ? `/api/drafts/${t.draftId}` : `/api/article-drafts/${t.articleDraftId}`;
    }
    async attachments(target) {
        return (await this.request("GET", `${this.ownerPath(target)}/attachments`)).attachments;
    }
    attachmentUrl(id) {
        return `${this.base}/api/attachments/${id}`;
    }
    /** multipart upload; `file` is a Blob (browser File) or anything FormData accepts. */
    async upload(target, file, name) {
        const form = new FormData();
        form.append("file", file, name);
        const res = await this.fetchFn(`${this.base}${this.ownerPath(target)}/attachments`, { method: "POST", credentials: "same-origin", headers: this.headers(), body: form });
        const json = (await res.json().catch(() => null));
        if (!res.ok)
            throw new ApiError(res.status, json?.error ?? "http_error", json?.message ?? `${res.status} ${res.statusText}`);
        return json;
    }
    async workflows() {
        return (await this.request("GET", "/api/workflows")).workflows;
    }
    async workflowRuns(id) {
        return (await this.request("GET", `/api/workflows/${id}/runs`)).runs;
    }
    async integrations() {
        return (await this.request("GET", "/api/integrations")).integrations;
    }
    async refs(issueId) {
        return (await this.request("GET", `/api/issues/${issueId}/refs`)).refs;
    }
    async savedSearches() {
        return (await this.request("GET", "/api/saved-searches")).savedSearches;
    }
    async drafts() {
        return (await this.request("GET", "/api/drafts")).drafts;
    }
    notifications(opts = {}) {
        const q = new URLSearchParams();
        if (opts.limit)
            q.set("limit", String(opts.limit));
        if (opts.before)
            q.set("before", String(opts.before));
        return this.request("GET", `/api/notifications${q.size ? `?${q}` : ""}`);
    }
    async notificationPrefs() {
        return (await this.request("GET", "/api/notification-prefs")).prefs;
    }
    async apiTokens() {
        return (await this.request("GET", "/api/tokens")).tokens;
    }
    // Knowledge Base (M11)
    async articles() {
        return (await this.request("GET", "/api/articles")).articles;
    }
    article(id) {
        return this.request("GET", `/api/articles/${id}`);
    }
    articleByKey(key) {
        return this.request("GET", `/api/articles/by-key/${encodeURIComponent(key)}`);
    }
    async articleComments(id) {
        return (await this.request("GET", `/api/articles/${id}/comments`)).comments;
    }
    async searchArticles(q, limit = 50) {
        return (await this.request("GET", `/api/articles/search?q=${encodeURIComponent(q)}&limit=${limit}`)).hits;
    }
    async articleDrafts() {
        return (await this.request("GET", "/api/article-drafts")).drafts;
    }
    // Time tracking and workflow actions
    async workItems(issueId) {
        return (await this.request("GET", `/api/issues/${issueId}/work-items`)).workItems;
    }
    async issueActions(issueId) {
        return (await this.request("GET", `/api/issues/${issueId}/actions`)).actions;
    }
    // Dashboards (M12)
    async dashboards() {
        return (await this.request("GET", "/api/dashboards")).dashboards;
    }
    dashboard(id) {
        return this.request("GET", `/api/dashboards/${id}`);
    }
    aggregate(q, by, limit = 10) {
        return this.request("GET", `/api/issues/aggregate?q=${encodeURIComponent(q)}&by=${encodeURIComponent(by)}&limit=${limit}`);
    }
    adminOverview() {
        return this.request("GET", "/api/admin/overview");
    }
    async command(name, input, mutationId) {
        const r = await this.request("POST", `/api/commands/${name}`, {
            ...(mutationId ? { mutationId } : {}),
            input,
        });
        return r.result;
    }
}
//# sourceMappingURL=http.js.map